import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import { useDispatch } from 'react-redux';
import { setSellerData } from './redux/authSlice';

// Context Providers
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { OrdersProvider } from './context/OrdersContext';

// Components
import PageTransition from './components/PageTransition';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load AppRoutes
const AppRoutes = lazy(() => import('./routes'));

// Lazy load Navbar components
const Navbar = lazy(() => import('./components/common/Navbar'));
const SecondaryNavbar = lazy(() => import('./components/common/SecondaryNavbar'));

function AppContent({ user, setUser, loading }) {
  const location = useLocation();

  // Check if current path is a seller route or auth page
  const isSellerRoute = location.pathname.startsWith('/seller');
  const isAuthPage = location.pathname.includes('/auth/');

  // Show navbar only for public pages and user routes (not seller routes or auth pages)
  const showNavbar = !isSellerRoute && !isAuthPage && !loading;

  return (
    <PageTransition>
      {showNavbar && (
        <Suspense fallback={<LoadingSpinner />}>
          <Navbar user={user} setUser={setUser} />
          <SecondaryNavbar user={user} />
        </Suspense>
      )}

      <div className={isAuthPage ? "" : "pt-0 lg:pt-10"}>
        <Suspense fallback={<LoadingSpinner />}>
          <AppRoutes user={user} setUser={setUser} loading={loading} />
        </Suspense>
      </div>
    </PageTransition>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  // Fetch user data from Firestore
  const fetchUserData = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return null;

    try {
      // First, check if user exists in sellers collection
      const sellerRef = doc(db, 'sellers', firebaseUser.uid);
      const sellerDoc = await getDoc(sellerRef);
      
      let userData = null;
      let role = 'customer';

      if (sellerDoc.exists()) {
        userData = sellerDoc.data();
        role = 'seller';
        
        // Sync email verification
        if (userData.emailVerified !== firebaseUser.emailVerified) {
          await updateDoc(sellerRef, {
            emailVerified: firebaseUser.emailVerified,
            updatedAt: new Date().toISOString()
          });
          userData.emailVerified = firebaseUser.emailVerified;
        }
        
        // Set seller data in Redux
        dispatch(setSellerData({ 
          ...userData, 
          userId: firebaseUser.uid 
        }));
      } else {
        // Check in users collection
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          userData = userDoc.data();
          role = userData.role || 'customer';
          
          // Sync email verification
          if (userData.emailVerified !== firebaseUser.emailVerified) {
            await updateDoc(userRef, {
              emailVerified: firebaseUser.emailVerified,
              updatedAt: new Date().toISOString()
            });
            userData.emailVerified = firebaseUser.emailVerified;
          }
        } else {
          // User document doesn't exist - this shouldn't happen with proper signup
          console.warn('User document not found for:', firebaseUser.uid);
          return null;
        }
      }

      const completeUserData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        role: role,
        accountStatus: userData?.accountStatus || 'active',
        ...userData
      };
      
      return completeUserData;
    } catch (err) {
      console.error('Error fetching user data:', err);
      return null;
    }
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    let unsubscribeAuth = null;

    const initAuth = () => {
      unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!isMounted) return;

        try {
          if (firebaseUser) {
            // Fetch complete user data
            const userData = await fetchUserData(firebaseUser);
            
            if (isMounted) {
              if (userData) {
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
              } else {
                // User exists in auth but not in Firestore - sign them out
                console.error('User document missing, signing out...');
                await auth.signOut();
                setUser(null);
                localStorage.removeItem('user');
              }
            }
          } else {
            if (isMounted) {
              setUser(null);
              localStorage.removeItem('user');
            }
          }
        } catch (err) {
          console.error('Auth state change error:', err);
          if (isMounted) {
            setUser(null);
            localStorage.removeItem('user');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      });
    };

    // Load cached user while auth initializes
    const cachedUser = localStorage.getItem('user');
    if (cachedUser && !user) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    initAuth();

    return () => {
      isMounted = false;
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserData]);


  return (
    <CartProvider>
      <WishlistProvider>
        <OrdersProvider>
          <Router>
            <ToastContainer 
              autoClose={3000}
              position="top-right"
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <AppContent user={user} setUser={setUser} loading={loading} />
          </Router>
        </OrdersProvider>
      </WishlistProvider>
    </CartProvider>
  );
}

export default App;